import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { 
  Search, 
  UserPlus, 
  Users, 
  Loader2,
  X
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export default function CandidateSelectionModal({
  isOpen,
  onClose,
  selectedCandidates,
  onCandidatesChange,
  availableCandidates,
  onSearch,
  searchTerm,
  loadingCandidates,
  onAddCandidate
}) {
  const { toast } = useToast();
  const [localSelectedCandidates, setLocalSelectedCandidates] = useState([]);
  const [localSearchTerm, setLocalSearchTerm] = useState("");

  // Initialize local state when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalSelectedCandidates([...selectedCandidates]);
      setLocalSearchTerm(searchTerm || "");
    }
  }, [isOpen, selectedCandidates, searchTerm]);

  // Handle candidate selection toggle
  const handleCandidateToggle = (candidate, checked) => {
    setLocalSelectedCandidates(prev => {
      if (checked) {
        // Add candidate if not already selected
        const isAlreadySelected = prev.some(c => c._id === candidate._id);
        if (!isAlreadySelected) {
          return [...prev, candidate];
        }
        return prev;
      } else {
        // Remove candidate
        return prev.filter(c => c._id !== candidate._id);
      }
    });
  };

  // Handle search input change
  const handleSearchChange = (value) => {
    setLocalSearchTerm(value);
    onSearch(value);
  };

  // Handle submit - apply changes
  const handleSubmit = () => {
    onCandidatesChange(localSelectedCandidates);
    onClose();
    
    toast({
      title: "Candidates Updated",
      description: `${localSelectedCandidates.length} candidate(s) selected`,
    });
  };

  // Handle cancel - revert changes
  const handleCancel = () => {
    setLocalSelectedCandidates([...selectedCandidates]);
    setLocalSearchTerm(searchTerm || "");
    onClose();
  };

  // Check if candidate is selected
  const isCandidateSelected = (candidate) => {
    return localSelectedCandidates.some(c => c._id === candidate._id);
  };

  // Handle add candidate
  const handleAddCandidateClick = () => {
    onAddCandidate();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Select Candidates
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex flex-col space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search candidates by name or email..."
              value={localSearchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10"
            />
            {loadingCandidates && (
              <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
            )}
          </div>

          {/* Add Candidate Button */}
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleAddCandidateClick}
              className="flex items-center gap-2"
            >
              <UserPlus className="h-4 w-4" />
              Add Candidate
            </Button>
          </div>

          {/* Selected Candidates Summary */}
          {localSelectedCandidates.length > 0 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <Badge 
                  variant="secondary" 
                  className="bg-blue-100 text-blue-800 border-blue-200 px-3 py-1"
                >
                  <Users className="h-4 w-4 mr-1" />
                  Selected: {localSelectedCandidates.length}
                </Badge>
                <div className="text-xs text-blue-600 max-w-md truncate">
                  {localSelectedCandidates.map(candidate => candidate.name).join(', ')}
                </div>
              </div>
            </div>
          )}

          {/* Candidates List */}
          <div className="flex-1 min-h-0 overflow-y-auto border rounded-lg">
            {loadingCandidates ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                <span>Searching candidates...</span>
              </div>
            ) : availableCandidates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {localSearchTerm ? (
                  <>
                    No candidates found for "{localSearchTerm}".
                    <br />
                    Try a different search term.
                  </>
                ) : (
                  <>
                    No candidates available.
                    <br />
                    Use the search box to find candidates or add a new one.
                  </>
                )}
              </div>
            ) : (
              <div className="p-4 space-y-3">
                {availableCandidates.map((candidate) => {
                  const isSelected = isCandidateSelected(candidate);
                  
                  return (
                    <div
                      key={candidate._id}
                      className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${
                        isSelected 
                          ? 'bg-blue-50 border-blue-200' 
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => handleCandidateToggle(candidate, checked)}
                        className="flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {candidate.name}
                        </div>
                        <div className="text-sm text-gray-500 truncate">
                          {candidate.email}
                        </div>
                      </div>
                      {isSelected && (
                        <Badge variant="secondary" className="bg-blue-100 text-blue-800 border-blue-200">
                          Selected
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Search Results Info */}
          {localSearchTerm && !loadingCandidates && (
            <div className="text-sm text-gray-600 px-1">
              {availableCandidates.length === 0 ? (
                <span className="text-gray-500">
                  No candidates found for "{localSearchTerm}"
                </span>
              ) : availableCandidates.length === 1 ? (
                <span className="text-green-600">
                  ✓ Found 1 candidate matching "{localSearchTerm}"
                </span>
              ) : (
                <span>
                  Found {availableCandidates.length} candidates matching "{localSearchTerm}"
                </span>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            Apply Selection ({localSelectedCandidates.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}